import React, { useState, useEffect } from 'react';
import {
    Card,
    Form,
    Input,
    Select,
    Button,
    message,
    Typography,
    Space,
    Row,
    Col,
    Divider,
    Alert,
    Modal
} from 'antd';
import {
    MessageOutlined,
    SendOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import feedbackService from '../services/feedbackService';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/Common/PageHeader';
import LoginRequired from '../components/Common/LoginRequired';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

const Feedback = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const { currentUser } = useSelector((state) => state.user);
    const navigate = useNavigate();

    // Check authentication when component mounts or user changes
    useEffect(() => {
        // Only show modal once when user is not logged in
        if (!currentUser) {
            const timer = setTimeout(() => {
                Modal.confirm({
                    title: 'Login Required',
                    icon: <ExclamationCircleOutlined />,
                    content: 'You need to be logged in to submit feedback. Please login to continue.',
                    okText: 'Yes, Login',
                    cancelText: 'Cancel',
                    onOk: () => {
                        navigate('/login', { state: { from: '/feedback' } });
                    },
                    onCancel: () => {
                        // Don't redirect on cancel, let them see the login required component
                    }
                });
            }, 300); // Small delay to prevent immediate redirect

            return () => clearTimeout(timer);
        }
    }, []); // Only run once on mount

    const handleSubmit = async (values) => {
        // Check if user is logged in before submitting
        if (!currentUser) {
            Modal.confirm({
                title: 'Login Required',
                icon: <ExclamationCircleOutlined />,
                content: 'You need to be logged in to submit feedback. Would you like to login now?',
                okText: 'Yes, Login',
                cancelText: 'Cancel',
                onOk: () => {
                    navigate('/login', { state: { from: '/feedback' } });
                }
            });
            return;
        }

        try {
            setLoading(true);
            const feedbackData = {
                type: values.type,
                subject: values.subject,
                message: values.message
            };

            const response = await feedbackService.submitFeedback(feedbackData);
            
            if (response.status === 'success') {
                // Redirect to thank you page
                navigate('/feedback/thank-you');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to submit feedback. Please try again.';
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const feedbackTypes = [
        { value: 'general', label: 'General Feedback' },
        { value: 'bug_report', label: 'Bug Report' },
        { value: 'feature_request', label: 'Feature Request' },
        { value: 'suggestion', label: 'Suggestion' },
        { value: 'complaint', label: 'Complaint' },
        { value: 'compliment', label: 'Compliment' },
        { value: 'other', label: 'Other' }
    ];

    // Don't render form if user is not logged in
    if (!currentUser) {
        return (
            <DashboardLayout>
                <LoginRequired
                    title="Login Required"
                    message="You need to be logged in to submit feedback. Please login to continue."
                    returnPath="/feedback"
                />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <PageHeader
                title="Feedback"
                description="We value your opinion! Share your thoughts, suggestions, or report any issues you've encountered."
            />

            <Row gutter={[24, 24]}>
                <Col xs={24} lg={16}>
                    <Card
                        className="form-section"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e8f4f8',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: '32px' }}
                    >
                        <div style={{ 
                            borderBottom: '2px solid #f0f7ff',
                            paddingBottom: 20,
                            marginBottom: 28,
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                            margin: '-32px -32px 28px -32px',
                            padding: '24px 32px'
                        }}>
                            <Space align="center" size="middle">
                                <div style={{
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                                }}>
                                    <MessageOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                        Share Your Feedback
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                        Help us improve by sharing your thoughts, suggestions, or reporting issues
                                    </Text>
                                </div>
                            </Space>
                        </div>

                        {currentUser && (
                            <Alert
                                message={
                                    <span>
                                        Submitting as: <strong>{currentUser.name || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()}</strong> ({currentUser.email})
                                    </span>
                                }
                                type="info"
                                showIcon
                                icon={<InfoCircleOutlined />}
                                style={{ marginBottom: 24, borderRadius: '8px' }}
                            />
                        )}

                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                            size="large"
                        >
                            <Form.Item
                                name="type"
                                label={<span style={{ fontWeight: 500, fontSize: '15px' }}>Feedback Type</span>}
                                rules={[{ required: true, message: 'Please select a feedback type' }]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        Select the category that best describes your feedback
                                    </Text>
                                }
                            >
                                <Select 
                                    placeholder="Select the type of feedback..."
                                    style={{ borderRadius: '8px' }}
                                >
                                    {feedbackTypes.map(type => (
                                        <Option key={type.value} value={type.value}>
                                            {type.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="subject"
                                label={<span style={{ fontWeight: 500, fontSize: '15px' }}>Subject</span>}
                                rules={[
                                    { required: true, message: 'Please enter a subject' },
                                    { max: 255, message: 'Subject must be less than 255 characters' }
                                ]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        A brief summary of your feedback (max 255 characters)
                                    </Text>
                                }
                            >
                                <Input
                                    prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
                                    placeholder="Brief summary of your feedback"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="message"
                                label={<span style={{ fontWeight: 500, fontSize: '15px' }}>Your Feedback</span>}
                                rules={[
                                    { required: true, message: 'Please enter your feedback' },
                                    { min: 10, message: 'Feedback must be at least 10 characters' },
                                    { max: 2000, message: 'Feedback must be less than 2000 characters' }
                                ]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        Please provide detailed feedback. The more information you share, the better we can help you.
                                    </Text>
                                }
                            >
                                <TextArea
                                    rows={6}
                                    placeholder="Please provide detailed feedback. The more information you share, the better we can help you."
                                    showCount
                                    maxLength={2000}
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>

                            <Divider style={{ margin: '24px 0', borderColor: '#e8f4f8' }} />

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    size="large"
                                    icon={<SendOutlined />}
                                    block
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        height: '44px',
                                        fontSize: '15px',
                                        boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                    }}
                                >
                                    {loading ? 'Submitting...' : 'Submit Feedback'}
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card
                        className="form-section"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e8f4f8',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: '24px' }}
                    >
                        <div style={{ 
                            borderBottom: '2px solid #fff7e6',
                            paddingBottom: 16,
                            marginBottom: 20,
                            background: 'linear-gradient(135deg, #fff7e6 0%, #ffffff 100%)',
                            margin: '-24px -24px 20px -24px',
                            padding: '20px 24px'
                        }}>
                            <Space align="center" size="small">
                                <div style={{
                                    background: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(250, 173, 20, 0.3)'
                                }}>
                                    <InfoCircleOutlined style={{ color: 'white', fontSize: '18px' }} />
                                </div>
                                <Title level={5} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                    Why Your Feedback Matters
                                </Title>
                            </Space>
                        </div>

                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <div>
                                <Title level={5} style={{ fontSize: '15px', fontWeight: 600, marginBottom: 8 }}>Help Us Improve</Title>
                                <Paragraph type="secondary" style={{ fontSize: '14px', marginBottom: 0, lineHeight: '1.6' }}>
                                    Your feedback helps us understand what's working well and what we can improve. 
                                    We read every submission and use it to make our platform better.
                                </Paragraph>
                            </div>

                            <Divider style={{ margin: '12px 0', borderColor: '#e8f4f8' }} />

                            <div>
                                <Title level={5} style={{ fontSize: '15px', fontWeight: 600, marginBottom: 8 }}>Response Time</Title>
                                <Paragraph type="secondary" style={{ fontSize: '14px', marginBottom: 0, lineHeight: '1.6' }}>
                                    We typically respond to feedback within 24-48 hours. For urgent issues, 
                                    please contact our support team directly.
                                </Paragraph>
                            </div>

                            <Divider style={{ margin: '12px 0', borderColor: '#e8f4f8' }} />

                            <div>
                                <Title level={5} style={{ fontSize: '15px', fontWeight: 600, marginBottom: 8 }}>What Happens Next?</Title>
                                <Paragraph type="secondary" style={{ fontSize: '14px', marginBottom: 0, lineHeight: '1.6' }}>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        <li style={{ marginBottom: 4 }}>Your feedback is reviewed by our team</li>
                                        <li style={{ marginBottom: 4 }}>We may follow up for more details</li>
                                        <li>Your suggestions help shape future updates</li>
                                    </ul>
                                </Paragraph>
                            </div>
                        </Space>
                    </Card>
                </Col>
            </Row>
        </DashboardLayout>
    );
};

export default Feedback;

